import {
  ArrowRight,
  Captions,
  CheckCircle2,
  ClipboardCopy,
  Download,
  FileText,
  Film,
  Layers3,
  Play,
  ShieldCheck,
  Sparkles,
  UploadCloud,
  Wand2
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { createVideoLead, generateVideoProject, loadPublicConfig, trackSiteEvent } from "./api";
import type { TurnstileApi } from "./turnstileClient";
import type { PublicConfig, VideoFactoryInput, VideoProject } from "./types";

const defaultInput: VideoFactoryInput = {
  audience: "solo founders building English-market AI tools",
  duration_seconds: 45,
  goal: "turn viewers into qualified visitors for a free AI tool",
  platform: "YouTube Shorts",
  source_path: "/ai-video-factory/",
  tone: "practical",
  topic: "AI video workflow for one-person companies"
};

const platforms = ["YouTube Shorts", "TikTok", "Instagram Reels", "LinkedIn"];
const tones = ["practical", "educational", "contrarian", "founder-led", "direct"];

const capabilityCards = [
  {
    body:
      "Move from a vague topic to a sharp opening, audience angle, narration and conversion goal.",
    icon: FileText,
    title: "Script that knows the outcome"
  },
  {
    body:
      "Break the video into scenes with timing, visuals, asset search directions and on-screen text.",
    icon: Layers3,
    title: "Storyboard before editing"
  },
  {
    body:
      "Package captions, hashtags, review notes and publishing checks before anything goes live.",
    icon: Captions,
    title: "Captions and review gate"
  }
];

const workflow = [
  ["01", "Give it the intent", "Topic, audience, platform, tone and the business action you want."],
  ["02", "Generate the packet", "Hook, script, scenes, captions and asset directions in one structured pass."],
  ["03", "Review and publish", "Human approval stays in the loop before recording, editing, ads or public posting."]
];

const modulePages = [
  {
    body:
      "Create English short-video scripts, storyboard packets and publishing notes for tools, tutorials and creator channels.",
    features: ["Hook and narration", "Scene-by-scene storyboard", "Captions and launch checklist"],
    slug: "",
    status: "Live MVP",
    title: "AI Video Factory"
  },
  {
    body:
      "Translate video ideas, scripts and subtitle packets while preserving context, positioning and call to action.",
    features: ["Localized script packet", "Subtitle translation plan", "Dubbing direction"],
    slug: "video-translator",
    status: "Validation",
    title: "AI Video Translator"
  },
  {
    body:
      "Turn long-form ideas into shorter clip packets with strong hooks, reusable scenes and platform-specific captions.",
    features: ["Clip candidate brief", "Scene timing plan", "Short-form caption pack"],
    slug: "clip-maker",
    status: "Validation",
    title: "AI Clip Maker"
  },
  {
    body:
      "Prepare subtitle and caption packets for videos that need a clean review step before publishing.",
    features: ["Caption variants", "SRT/VTT export plan", "Rights and review checklist"],
    slug: "subtitle-generator",
    status: "Validation",
    title: "AI Subtitle Generator"
  }
];

function modulePath(slug: string) {
  return slug ? `/ai-video-factory/${slug}/` : "/ai-video-factory/";
}

function VideoNav() {
  return (
    <nav className="public-nav video-nav">
      <a className="video-brand" href="/ai-video-factory/" aria-label="AI Video Factory home">
        <span className="public-logo">VF</span>
        <strong>AI Video Factory</strong>
      </a>
      <nav aria-label="Video factory navigation">
        <a href="/ai-video-factory/#results">Results</a>
        <a href="/ai-video-factory/#workflow">Workflow</a>
        <a href="/ai-video-factory/#generator">Generator</a>
        <a className="nav-cta" href="/ai-video-factory/#generator">
          Start Free
        </a>
      </nav>
    </nav>
  );
}

function downloadJson(project: VideoProject) {
  const blob = new Blob([JSON.stringify(project, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${project.id}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

function getAttribution() {
  const params = new URLSearchParams(window.location.search);

  return {
    source_path: `${window.location.pathname}${window.location.search}`,
    utm_campaign: params.get("utm_campaign"),
    utm_medium: params.get("utm_medium"),
    utm_source: params.get("utm_source")
  };
}

function injectWebAnalytics(token: string) {
  if (document.querySelector(`script[data-siteops-web-analytics="true"]`)) return;

  const script = document.createElement("script");
  script.defer = true;
  script.dataset.cfBeacon = JSON.stringify({ token });
  script.dataset.siteopsWebAnalytics = "true";
  script.src = "https://static.cloudflareinsights.com/beacon.min.js";
  document.head.appendChild(script);
}

function storageCopy(project: VideoProject) {
  if (project.storage?.status === "stored" && project.storage.key) {
    return `Saved to private R2 storage: ${project.storage.key}`;
  }

  if (project.storage?.status === "failed") {
    return "Saved to the database. R2 storage failed and needs operator review.";
  }

  return "Saved to the database. R2 storage is not configured for this environment.";
}

export function VideoFactoryPage() {
  const [input, setInput] = useState<VideoFactoryInput>(defaultInput);
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [leadEmail, setLeadEmail] = useState("");
  const [leadRole, setLeadRole] = useState("solo founder");
  const [leadUrgency, setLeadUrgency] = useState("this month");
  const [leadUseCase, setLeadUseCase] = useState(
    "I want to turn founder-led product ideas into short videos without hiring an editor."
  );
  const [leadWebsite, setLeadWebsite] = useState("");
  const [leadStatus, setLeadStatus] = useState("Leave an email if you want the launch checklist and follow-up.");
  const [leadLoading, setLeadLoading] = useState(false);
  const [project, setProject] = useState<VideoProject | null>(null);
  const [status, setStatus] = useState("Ready to generate a review-ready video packet.");
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const projectJson = useMemo(() => (project ? JSON.stringify(project, null, 2) : ""), [project]);
  const turnstileRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const currentModule = modulePages.find(
    (item) => item.slug && window.location.pathname === modulePath(item.slug)
  );

  function track(eventName: string, target?: string, metadata: Record<string, unknown> = {}) {
    const attribution = getAttribution();
    void trackSiteEvent({
      event_name: eventName,
      metadata,
      referrer: document.referrer,
      site_id: "site_ai_video_factory",
      target,
      ...attribution
    }).catch(() => undefined);
  }

  useEffect(() => {
    void loadPublicConfig()
      .then((next) => {
        setConfig(next);
        if (next.analytics.webAnalyticsToken) {
          injectWebAnalytics(next.analytics.webAnalyticsToken);
        }
      })
      .catch(() => {
        setConfig({
          analytics: {
            customEventsEnabled: true,
            mode: "not_configured",
            webAnalyticsToken: null
          },
          turnstile: {
            enabled: false,
            siteKey: null
          }
        });
      });

    track("video_page_view", "ai-video-factory");
  }, []);

  useEffect(() => {
    const siteKey = config?.turnstile.siteKey;
    const element = turnstileRef.current;
    if (!siteKey || !element || turnstileWidgetId.current) return;
    const siteKeyValue = siteKey;

    function renderTurnstile() {
      if (!window.turnstile || !turnstileRef.current || turnstileWidgetId.current) return;

      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        callback: (token) => setTurnstileToken(token),
        sitekey: siteKeyValue,
        theme: "light"
      });
    }

    const existing = document.querySelector<HTMLScriptElement>("script[data-siteops-turnstile='true']");

    if (window.turnstile) {
      renderTurnstile();
      return;
    }

    if (existing) {
      existing.addEventListener("load", renderTurnstile);
      return () => existing.removeEventListener("load", renderTurnstile);
    }

    const script = document.createElement("script");
    script.async = true;
    script.defer = true;
    script.dataset.siteopsTurnstile = "true";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.addEventListener("load", renderTurnstile);
    document.head.appendChild(script);

    return () => script.removeEventListener("load", renderTurnstile);
  }, [config?.turnstile.siteKey]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setStatus("Generating script, scenes, captions and asset directions...");
    track("video_generate_submit", "generator", {
      platform: input.platform,
      tone: input.tone
    });

    try {
      const result = await generateVideoProject({ ...input, source_path: getAttribution().source_path });
      setProject(result.project);
      setStatus(`Generated with ${result.project.engine}. ${storageCopy(result.project)}`);
      track("video_generate_success", "generator", {
        engine: result.project.engine,
        platform: result.project.platform,
        project_id: result.project.id,
        r2_status: result.project.storage?.status ?? "unknown",
        scenes: result.project.scenes.length
      });
    } catch {
      setStatus("Generation failed. Check the API route, D1 migration and Cloudflare deployment.");
      track("video_generate_error", "generator");
    } finally {
      setLoading(false);
    }
  }

  async function copyJson() {
    if (!projectJson) return;
    await navigator.clipboard.writeText(projectJson);
    setStatus("Copied the production packet JSON.");
    track("video_packet_copy", "packet", { project_id: project?.id });
  }

  async function submitLead(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLeadLoading(true);
    setLeadStatus("Saving your request...");
    track("video_lead_submit", "lead-form", { has_project: Boolean(project?.id) });

    try {
      const attribution = getAttribution();
      const result = await createVideoLead({
        email: leadEmail,
        project_id: project?.id ?? null,
        role: leadRole,
        site_id: "site_ai_video_factory",
        turnstile_token: turnstileToken,
        urgency: leadUrgency,
        use_case: leadUseCase,
        website: leadWebsite,
        ...attribution
      });

      setLeadStatus(
        result.turnstile_status === "verified"
          ? "Saved. Turnstile verified this request."
          : "Saved. Turnstile is not configured yet, so this request is marked for manual review."
      );
      setLeadEmail("");
      setLeadUseCase("");
      setTurnstileToken(null);
      if (turnstileWidgetId.current) window.turnstile?.reset(turnstileWidgetId.current);
      track("video_lead_success", "lead-form", {
        lead_id: result.id,
        turnstile_status: result.turnstile_status
      });
    } catch {
      setLeadStatus("Could not save this request. Check the email and try again.");
      track("video_lead_error", "lead-form");
    } finally {
      setLeadLoading(false);
    }
  }

  if (currentModule) {
    return (
      <main className="public-page video-page video-page-premium">
        <VideoNav />
        <section className="video-module-hero">
          <div className="video-hero-shade" />
          <div className="video-module-content">
            <p className="public-eyebrow">{currentModule.status}</p>
            <h1>{currentModule.title}</h1>
            <p>{currentModule.body}</p>
            <div className="hero-actions">
              <a
                className="primary-link"
                href="/ai-video-factory/#generator"
                onClick={() => track("video_cta_click", "module_try_generator", { module: currentModule.slug })}
              >
                Try the generator
                <ArrowRight size={17} />
              </a>
              <a
                className="secondary-link light-link"
                href="/ai-video-factory/#workflow"
                onClick={() => track("video_cta_click", "module_see_workflow", { module: currentModule.slug })}
              >
                See workflow
              </a>
            </div>
          </div>
        </section>

        <section className="video-outcome-section compact-outcome">
          <div className="video-section-heading">
            <p className="public-eyebrow">What it produces</p>
            <h2>A focused packet, not a scattered tool list.</h2>
          </div>
          <div className="video-capability-grid">
            {currentModule.features.map((feature) => (
              <article key={feature}>
                <CheckCircle2 size={20} />
                <h3>{feature}</h3>
                <p>
                  Built for English content teams that need a clear planning layer before recording,
                  editing or paying for media infrastructure.
                </p>
              </article>
            ))}
          </div>
        </section>

        <section className="video-final-cta premium-final">
          <p className="public-eyebrow">Start with the working MVP</p>
          <h2>Generate the packet first. Add rendering only after real demand.</h2>
          <a
            className="primary-link"
            href="/ai-video-factory/#generator"
            onClick={() => track("video_cta_click", "module_final_generate", { module: currentModule.slug })}
          >
            Generate a packet
            <ArrowRight size={17} />
          </a>
        </section>

        <footer className="public-footer">
          <span>AI Video Factory</span>
          <span>Public content in English. Operator review required before publishing.</span>
        </footer>
      </main>
    );
  }

  return (
    <main className="public-page video-page video-page-premium">
      <VideoNav />

      <section className="video-hero">
        <div className="video-hero-shade" />
        <div className="video-hero-content">
          <p className="public-eyebrow">AI video creation system</p>
          <h1>Turn rough ideas into review-ready video assets.</h1>
          <p>
            AI Video Factory turns a topic into a hook, script, storyboard, captions, asset
            directions and launch checklist before you open a video editor.
          </p>
          <div className="hero-actions">
            <a
              className="primary-link"
              href="#generator"
              onClick={() => track("video_cta_click", "hero_generate")}
            >
              Generate a packet
              <ArrowRight size={17} />
            </a>
            <a
              className="secondary-link light-link"
              href="#results"
              onClick={() => track("video_cta_click", "hero_results")}
            >
              See the output
            </a>
          </div>
          <div className="video-hero-trust">
            <span>Script</span>
            <span>Storyboard</span>
            <span>Captions</span>
            <span>Review checklist</span>
          </div>
        </div>
      </section>

      <section className="video-proof-band" aria-label="AI Video Factory proof points">
        <span>
          <CheckCircle2 size={16} />
          English-market content
        </span>
        <span>
          <CheckCircle2 size={16} />
          Human review before publishing
        </span>
        <span>
          <CheckCircle2 size={16} />
          Cost-controlled MVP
        </span>
      </section>

      <section className="video-outcome-section" id="results">
        <div className="video-section-heading">
          <p className="public-eyebrow">The result</p>
          <h2>A polished production brief instead of another empty prompt box.</h2>
          <p>
            The product focuses on the highest-leverage layer of video creation: deciding what to
            say, how to frame it, which scenes to build, and what must be checked before publishing.
          </p>
        </div>

        <div className="video-outcome-layout">
          <figure className="video-studio-image">
            <img
              alt="Creator editing a video timeline in a premium studio workspace"
              src="/assets/ai-video-studio.jpg"
            />
          </figure>

          <div className="video-capability-grid">
            {capabilityCards.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title}>
                  <Icon size={22} />
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="video-tool-shell" id="generator">
        <div className="video-generator-intro">
          <p className="public-eyebrow">Try the MVP</p>
          <h2>Build a short-video packet in one pass.</h2>
          <p>
            This working version creates the production layer first. That keeps the product fast,
            useful and cost controlled before adding paid media processing.
          </p>
          <ul>
            <li>
              <CheckCircle2 size={17} />
              Best for YouTube Shorts, TikTok, Reels and LinkedIn clips.
            </li>
            <li>
              <CheckCircle2 size={17} />
              Saves structured generations to the SiteOps database.
            </li>
            <li>
              <CheckCircle2 size={17} />
              Keeps publishing decisions under human control.
            </li>
          </ul>
        </div>

        <form className="video-generator-panel" onSubmit={submit}>
          <div className="panel-title">
            <Wand2 size={22} />
            <div>
              <strong>Generate a video packet</strong>
              <span>English content for public channels and ads.</span>
            </div>
          </div>

          <label>
            Topic
            <input
              value={input.topic}
              onChange={(event) => setInput({ ...input, topic: event.target.value })}
              required
            />
          </label>

          <label>
            Audience
            <input
              value={input.audience}
              onChange={(event) => setInput({ ...input, audience: event.target.value })}
              required
            />
          </label>

          <div className="video-form-row">
            <label>
              Platform
              <select
                value={input.platform}
                onChange={(event) => setInput({ ...input, platform: event.target.value })}
              >
                {platforms.map((platform) => (
                  <option key={platform} value={platform}>
                    {platform}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Tone
              <select
                value={input.tone}
                onChange={(event) => setInput({ ...input, tone: event.target.value })}
              >
                {tones.map((tone) => (
                  <option key={tone} value={tone}>
                    {tone}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Seconds
              <input
                min="20"
                max="90"
                type="number"
                value={input.duration_seconds}
                onChange={(event) =>
                  setInput({ ...input, duration_seconds: Number(event.target.value) })
                }
              />
            </label>
          </div>

          <label>
            Conversion goal
            <textarea
              value={input.goal}
              onChange={(event) => setInput({ ...input, goal: event.target.value })}
              required
            />
          </label>

          <input
            className="website-field"
            tabIndex={-1}
            autoComplete="off"
            name="website"
            onChange={(event) => setInput({ ...input, website: event.target.value })}
          />

          <button type="submit" disabled={loading}>
            <Sparkles size={18} />
            {loading ? "Generating..." : "Generate production packet"}
          </button>
          <p className="tool-status">{status}</p>
        </form>
      </section>

      <section className="video-output" id="packet">
        <div className="packet-header">
          <div>
            <p className="public-eyebrow">Production packet</p>
            <h2>{project?.title ?? "Generate once to create the first packet"}</h2>
          </div>
          {project && (
            <div className="packet-actions">
              <button className="secondary-button" onClick={copyJson} type="button">
                <ClipboardCopy size={16} />
                Copy JSON
              </button>
              <button
                className="secondary-button"
                onClick={() => {
                  track("video_packet_download", "packet", { project_id: project.id });
                  downloadJson(project);
                }}
                type="button"
              >
                <Download size={16} />
                Download
              </button>
            </div>
          )}
        </div>

        {!project && (
          <div className="empty-packet">
            <Film size={28} />
            <p>
              The current MVP generates the production plan before rendering video. This is the
              disciplined layer before adding model costs, media uploads, TTS and video export.
            </p>
          </div>
        )}

        {project && (
          <>
            <div className="packet-grid">
              <article>
                <Film size={20} />
                <span>Hook</span>
                <strong>{project.hook}</strong>
              </article>
              <article>
                <FileText size={20} />
                <span>Positioning</span>
                <strong>{project.positioning}</strong>
              </article>
              <article>
                <Play size={20} />
                <span>Format</span>
                <strong>
                  {project.platform} / {project.duration_seconds}s / {project.engine}
                </strong>
              </article>
              <article>
                <UploadCloud size={20} />
                <span>Storage</span>
                <strong>{storageCopy(project)}</strong>
              </article>
            </div>

            <div className="storyboard-grid">
              {project.scenes.map((scene) => (
                <article className="scene-card" key={`${scene.start}-${scene.end}`}>
                  <div className="scene-frame">
                    <span>{scene.on_screen_text}</span>
                  </div>
                  <div>
                    <strong>
                      {scene.start} - {scene.end}
                    </strong>
                    <p>{scene.narration}</p>
                    <small>{scene.visual}</small>
                  </div>
                  <code>{scene.asset_query}</code>
                </article>
              ))}
            </div>

            <section className="packet-columns">
              <article>
                <h3>Captions</h3>
                {project.captions.map((caption) => (
                  <p key={caption}>{caption}</p>
                ))}
                <div className="hashtag-row">
                  {project.hashtags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </article>
              <article>
                <h3>Publishing Checklist</h3>
                <ul>
                  {project.publishing_checklist.map((item) => (
                    <li key={item}>
                      <ShieldCheck size={16} />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
              <article>
                <h3>Risk Notes</h3>
                <p>{project.monetization_angle}</p>
                <ul>
                  {project.risk_notes.map((item) => (
                    <li key={item}>
                      <ShieldCheck size={16} />
                      {item}
                    </li>
                  ))}
                </ul>
              </article>
            </section>
          </>
        )}
      </section>

      <section className="video-lead-section" id="lead">
        <div className="video-lead-copy">
          <p className="public-eyebrow">Save the signal</p>
          <h2>Want the launch checklist and next version?</h2>
          <p>
            Leave your email and the video workflow you are trying to build. This records demand
            before we add paid media infrastructure, credits or rendering.
          </p>
          <div className="video-lead-proof">
            <span>
              <CheckCircle2 size={16} />
              No auto-publishing
            </span>
            <span>
              <CheckCircle2 size={16} />
              Manual follow-up
            </span>
          </div>
        </div>

        <form className="video-lead-card" onSubmit={submitLead}>
          <label>
            Work email
            <input
              autoComplete="email"
              onChange={(event) => setLeadEmail(event.target.value)}
              placeholder="you@example.com"
              required
              type="email"
              value={leadEmail}
            />
          </label>

          <div className="video-form-row lead-row">
            <label>
              Role
              <select onChange={(event) => setLeadRole(event.target.value)} value={leadRole}>
                <option value="solo founder">Solo founder</option>
                <option value="creator">Creator</option>
                <option value="agency">Agency</option>
                <option value="marketer">Marketer</option>
              </select>
            </label>

            <label>
              Timeline
              <select onChange={(event) => setLeadUrgency(event.target.value)} value={leadUrgency}>
                <option value="this week">This week</option>
                <option value="this month">This month</option>
                <option value="researching">Researching</option>
              </select>
            </label>
          </div>

          <label>
            What are you trying to create?
            <textarea
              onChange={(event) => setLeadUseCase(event.target.value)}
              required
              value={leadUseCase}
            />
          </label>

          <input
            autoComplete="off"
            className="website-field"
            name="website"
            onChange={(event) => setLeadWebsite(event.target.value)}
            tabIndex={-1}
            value={leadWebsite}
          />

          {config?.turnstile.siteKey && <div className="turnstile-slot" ref={turnstileRef} />}

          <button disabled={leadLoading} type="submit">
            <Sparkles size={18} />
            {leadLoading ? "Saving..." : "Request the checklist"}
          </button>
          <p className="tool-status">{leadStatus}</p>
        </form>
      </section>

      <section className="video-workflow-section" id="workflow">
        <div className="video-section-heading">
          <p className="public-eyebrow">Workflow</p>
          <h2>From rough idea to a usable production brief.</h2>
        </div>
        <div className="video-workflow-grid">
          {workflow.map(([step, title, body]) => (
            <article key={step}>
              <span>{step}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="video-final-cta premium-final">
        <div>
          <UploadCloud size={26} />
          <p className="public-eyebrow">Cloudflare-first, cost disciplined</p>
          <h2>Start with a packet people actually use. Add paid media infrastructure later.</h2>
        </div>
        <a
          className="primary-link"
          href="#generator"
          onClick={() => track("video_cta_click", "final_generate")}
        >
          Generate a packet
          <ArrowRight size={17} />
        </a>
      </section>

      <footer className="public-footer">
        <span>AI Video Factory</span>
        <span>Public content in English. Operator review required before publishing.</span>
      </footer>
    </main>
  );
}
